import { useState, useEffect } from 'react';
import * as socialBookmarkingService from '../../../services/socialBookmarkingService';
import type { BookmarkItem, SocialBookmarkingModuleData } from '../../../services/socialBookmarkingService';
import { downloadS3ImageProxy } from '../../../services/web2Service';
import * as discoveryService from '../../../services/discoveryService';
import type { DiscoveredAsset } from '../../../services/discoveryService';

interface SocialBookmarkingDetailProps {
  onBack: () => void;
  darkMode: boolean;
}

export default function SocialBookmarkingDetail({ onBack, darkMode }: SocialBookmarkingDetailProps) {
  const [activeTab, setActiveTab] = useState<'new' | 'existing'>('new');

  // API-driven state
  const [moduleData, setModuleData] = useState<SocialBookmarkingModuleData | null>(null);
  const [loadingData, setLoadingData] = useState(true);
  const [selectedPages, setSelectedPages] = useState<Set<string>>(new Set());
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationId, setGenerationId] = useState<string | null>(null);
  const [generatedBookmarks, setGeneratedBookmarks] = useState<BookmarkItem[]>([]);
  const [bookmarksGenerated, setBookmarksGenerated] = useState(false);

  // Edit state
  const [editingBookmarkId, setEditingBookmarkId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editTags, setEditTags] = useState('');

  // Drawer state
  const [drawerBookmark, setDrawerBookmark] = useState<BookmarkItem | null>(null);
  const [drawerMode, setDrawerMode] = useState<'view' | 'edit'>('view');
  const [isSaving, setIsSaving] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Audit / Existing-site scan state
  const [auditScanId, setAuditScanId] = useState<string | null>(null);
  const [isAuditing, setIsAuditing] = useState(false);
  const [discoveredAssets, setDiscoveredAssets] = useState<any[]>([]);
  const [auditCompleted, setAuditCompleted] = useState(false);

  // UI State
  const [expandedImageUrl, setExpandedImageUrl] = useState<string | null>(null);

  // Active Selection & Asset Checklist State
  const [selectedBookmark, setSelectedBookmark] = useState<BookmarkItem | null>(null);
  const [checkedPlatforms, setCheckedPlatforms] = useState<Record<string, boolean>>({});
  const [isMarkingComplete, setIsMarkingComplete] = useState(false);
  const [markCompleteSuccess, setMarkCompleteSuccess] = useState(false);

  // Synchronize selectedBookmark with refreshed generatedBookmarks
  useEffect(() => {
    if (generatedBookmarks.length > 0) {
      if (!selectedBookmark) {
        const valid = generatedBookmarks.find(b => b.status !== 'FAILED' && b.title && b.title.trim() !== '');
        setSelectedBookmark(valid || generatedBookmarks[0]);
      } else {
        const updated = generatedBookmarks.find(b => b.id === selectedBookmark.id);
        if (updated) {
          setSelectedBookmark(updated);
        }
      }
    }
  }, [generatedBookmarks]);

  // Sync checkedPlatforms state with selectedBookmark's published_platforms
  useEffect(() => {
    if (selectedBookmark && moduleData?.platforms) {
      const initialChecked: Record<string, boolean> = {};
      const published = selectedBookmark.published_platforms || [];
      moduleData.platforms.forEach(p => {
        initialChecked[p.platform_name] = published.includes(p.platform_name);
      });
      setCheckedPlatforms(initialChecked);
    } else {
      setCheckedPlatforms({});
    }
  }, [selectedBookmark, moduleData]);

  const handleTogglePlatformCheckbox = (platformName: string) => {
    setCheckedPlatforms(prev => ({
      ...prev,
      [platformName]: !prev[platformName]
    }));
  };

  const handleMarkCompletePerBookmark = async () => {
    if (!selectedBookmark) return;
    setIsMarkingComplete(true);
    try {
      const selectedPlatforms = Object.keys(checkedPlatforms).filter(p => checkedPlatforms[p]);
      const updated = await socialBookmarkingService.updateBookmark(selectedBookmark.id, {
        published_platforms: selectedPlatforms
      });
      
      setGeneratedBookmarks(prev => prev.map(b => b.id === updated.id ? updated : b));
      setSelectedBookmark(updated);
      setMarkCompleteSuccess(true);
      setTimeout(() => setMarkCompleteSuccess(false), 3000);
    } catch (err) {
      console.error('Error marking complete for bookmark:', err);
    } finally {
      setIsMarkingComplete(false);
    }
  };

  const cardBg = darkMode ? 'bg-gray-800' : 'bg-white';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-500';

  // Fallback hardcoded bookmark sites (icons) for display
  const bookmarkSiteIcons: Record<string, string> = {
    'Reddit': 'ri-reddit-line',
    'Pinterest': 'ri-pinterest-line',
    'Mix': 'ri-share-line',
    'Diigo': 'ri-bookmark-3-line',
    'Slashdot': 'ri-terminal-box-line',
    'Pearltrees': 'ri-git-branch-line',
    'Scoop.it': 'ri-newspaper-line',
    'Pocket': 'ri-archive-line',
    'Folkd': 'ri-links-line',
  };

  useEffect(() => {
    loadModuleData();
    loadExistingBookmarks();
  }, []);

  const loadExistingBookmarks = async () => {
    try {
      const bookmarks = await socialBookmarkingService.getAllBookmarks();
      if (bookmarks && bookmarks.length > 0) {
        setGeneratedBookmarks(bookmarks);
        setBookmarksGenerated(true);
      }
    } catch (err) {
      console.error('Error loading existing bookmarks:', err);
    }
  };

  // Poll for generation results
  useEffect(() => {
    if (!generationId || !isGenerating) return;
    const interval = setInterval(async () => {
      try {
        const results = await socialBookmarkingService.getGeneratedBookmarks(generationId);
        if (results.length > 0) {
          setGeneratedBookmarks(results);
          const allDone = results.every(b => b.status === 'READY' || b.status === 'FAILED');
          if (allDone) {
            setIsGenerating(false);
            setBookmarksGenerated(true);
          }
        }
      } catch (err) {
        console.error('Error polling bookmarks:', err);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [generationId, isGenerating]);

  // Poll for audit results
  useEffect(() => {
    if (!auditScanId || !isAuditing) return;
    const interval = setInterval(async () => {
      try {
        const statusData = await discoveryService.getSocialBookmarkScanStatus(auditScanId);
        if (statusData.status === 'COMPLETED') {
          if (statusData.assets) {
            setDiscoveredAssets(statusData.assets);
          }
          setIsAuditing(false);
          setAuditCompleted(true);
        } else if (statusData.status === 'FAILED') {
          setIsAuditing(false);
          setAuditCompleted(true);
          console.error('Audit failed:', statusData.error_detail);
        }
      } catch (err) {
        console.error('Error polling audit status:', err);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [auditScanId, isAuditing]);

  const loadModuleData = async () => {
    setLoadingData(true);
    try {
      const data = await socialBookmarkingService.getModuleData();
      setModuleData(data);
      if (data.is_new_website !== undefined) {
        setActiveTab(data.is_new_website ? 'new' : 'existing');
      }
    } catch (err) {
      console.error('Error loading module data:', err);
    } finally {
      setLoadingData(false);
    }
  };

  const togglePageSelection = (url: string) => {
    if (selectedPages.has(url)) {
      setSelectedPages(new Set());
    } else {
      setSelectedPages(new Set([url]));
    }
  };

  const handleGenerate = async () => {
    if (selectedPages.size === 0) return;
    setIsGenerating(true);
    setGeneratedBookmarks([]);
    setBookmarksGenerated(false);
    try {
      const res = await socialBookmarkingService.generateBookmarks({
        page_ids: Array.from(selectedPages),
      });
      setGenerationId(res.generation_id);
    } catch (err) {
      console.error('Error starting generation:', err);
      setIsGenerating(false);
    }
  };

  const handleAudit = async () => {
    setIsAuditing(true);
    setAuditCompleted(false);
    setDiscoveredAssets([]);
    try {
      const userId = localStorage.getItem('user_id');
      if (!userId) {
        console.error('No user ID found for audit');
        setIsAuditing(false);
        return;
      }
      const res = await discoveryService.initiateSocialBookmarkScan(userId);
      setAuditScanId(res.scan_id);
    } catch (err) {
      console.error('Error initiating audit:', err);
      setIsAuditing(false);
    }
  };

  const openDrawer = (bm: BookmarkItem, mode: 'view' | 'edit') => {
    setDrawerBookmark(bm);
    setDrawerMode(mode);
    setEditTitle(bm.title || '');
    setEditDesc(bm.description || '');
    setEditTags(bm.tags?.join(', ') || '');
  };

  const closeDrawer = () => {
    setDrawerBookmark(null);
    setDrawerMode('view');
    setCopiedField(null);
  };

  const fallbackCopy = (text: string) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.position = "fixed";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try { document.execCommand("copy"); } catch (e) {}
    document.body.removeChild(textArea);
  };

  const handleCopyField = (text: string, field: string) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
    } else {
      fallbackCopy(text);
    }
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleCopyAll = (bm: BookmarkItem) => {
    const text = [
      `Title: ${bm.title || ''}`,
      `Description: ${bm.description || ''}`,
      `Tags: ${(bm.tags || []).join(', ')}`,
      `URL: ${bm.target_url || ''}`,
    ].join('\n\n');
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
    } else {
      fallbackCopy(text);
    }
    setCopiedField('all');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const saveFromDrawer = async () => {
    if (!drawerBookmark) return;
    setIsSaving(true);
    try {
      const tagsArray = editTags.split(',').map(t => t.trim()).filter(Boolean);
      const updated = await socialBookmarkingService.updateBookmark(drawerBookmark.id, {
        title: editTitle,
        description: editDesc,
        tags: tagsArray,
      });
      setGeneratedBookmarks(prev => prev.map(b => (b.id === drawerBookmark.id ? updated : b)));
      setDrawerBookmark(updated);
      setDrawerMode('view');
    } catch (err) {
      console.error('Error updating bookmark:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // Derive tags from API or use fallback
  const tags = moduleData?.project_context.keywords ?? ['SEO', 'digital-marketing', 'content-strategy', 'local-seo', 'google-ranking', 'backlinks', 'on-page-seo', 'keyword-research'];

  // Derive platforms from API or use fallback
  const platformList = (moduleData?.platforms || [])
    .slice()
    .sort((a, b) => b.da_score - a.da_score)
    .slice(0, 6);

  if (loadingData) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="flex items-center space-x-2 text-sm font-medium text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          <span>Back to Off-Page Content</span>
        </button>
      </div>

      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0 border border-blue-200 dark:border-blue-800">
          <i className="ri-bookmark-line text-blue-600 dark:text-blue-400 text-xl"></i>
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Social Bookmarking</h2>
            <button
              className="text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors cursor-pointer group relative z-50"
              onClick={(e) => {
                e.stopPropagation();
                const t = document.getElementById('social-bookmarking-tooltip');
                if (t) {
                  t.classList.toggle('opacity-0');
                  t.classList.toggle('opacity-100');
                  setTimeout(() => {
                    t.classList.add('opacity-0');
                    t.classList.remove('opacity-100');
                  }, 4000);
                }
              }}
            >
              <i className="ri-information-line text-lg"></i>
              <div
                id="social-bookmarking-tooltip"
                className="absolute left-0 top-full mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-left p-4 rounded-xl shadow-xl opacity-0 pointer-events-none transition-opacity z-50 leading-relaxed"
              >
                <p className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <i className="ri-error-warning-line text-blue-600 dark:text-blue-400"></i>
                  Prerequisite Inputs
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <i className="ri-checkbox-circle-fill text-teal-500 mt-0.5"></i>
                    <span>Target page URLs to bookmark</span>
                  </li>
                  <li className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <i className="ri-checkbox-circle-fill text-teal-500 mt-0.5"></i>
                    <span>Page titles for each URL</span>
                  </li>
                  <li className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <i className="ri-checkbox-circle-fill text-teal-500 mt-0.5"></i>
                    <span>Short descriptions (50–150 chars)</span>
                  </li>
                  <li className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <i className="ri-checkbox-circle-fill text-teal-500 mt-0.5"></i>
                    <span>Relevant tags / keywords</span>
                  </li>
                </ul>
              </div>
            </button>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Submit your key URLs to high-DA social bookmarking sites with keyword-rich summaries.</p>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="inline-flex bg-gray-100 dark:bg-gray-800 rounded-full p-1">
        <button
          onClick={() => setActiveTab('new')}
          disabled={moduleData?.is_new_website === false}
          className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap flex items-center ${
            moduleData?.is_new_website === false ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          } ${
            activeTab === 'new'
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <i className="ri-add-circle-line mr-1.5"></i>
          New Website
          {moduleData?.is_new_website === false && <i className="ri-lock-line ml-1.5"></i>}
        </button>
        <button
          onClick={() => setActiveTab('existing')}
          disabled={moduleData?.is_new_website === true}
          className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap flex items-center ${
            moduleData?.is_new_website === true ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          } ${
            activeTab === 'existing'
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <i className="ri-refresh-line mr-1.5"></i>
          Existing Website
          {moduleData?.is_new_website === true && <i className="ri-lock-line ml-1.5"></i>}
        </button>
      </div>


      {/* Tag Taxonomy — API-driven */}
      <div className={`${cardBg} rounded-xl border ${borderColor} p-5`}>
        <h3 className={`text-sm font-semibold ${textPrimary} mb-3 flex items-center gap-2`}>
          <i className="ri-price-tag-3-line text-blue-600 dark:text-blue-400"></i>
          Selected Keywords
        </h3>
        <div className="flex flex-wrap gap-2">
          {tags.map((tag) => (
            <span
              key={tag}
              className="px-3 py-1.5 text-xs font-medium rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>

      {/* Bookmark Sites — API-driven */}
      <div className={`${cardBg} rounded-xl border ${borderColor} p-5`}>
        <h3 className={`text-sm font-semibold ${textPrimary} mb-4 flex items-center gap-2`}>
          <i className="ri-global-line text-blue-600 dark:text-blue-400"></i>
          High-DA Bookmarking Sites
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {platformList.map((site) => {
            const icon = bookmarkSiteIcons[site.platform_name] || 'ri-bookmark-line';
            return (
              <div key={site.platform_url} className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
                <div className="w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                  <i className={`${icon} text-blue-600 dark:text-blue-400`}></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{site.platform_name}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">DA {site.da_score}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ─── Audit Existing Bookmarks ─── */}
      {activeTab === 'existing' && (
        <div className={`${cardBg} rounded-xl border ${borderColor} p-5 mt-5`}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
            <div>
              <h3 className={`text-sm font-semibold ${textPrimary} flex items-center gap-2`}>
                <i className="ri-radar-line text-blue-600 dark:text-blue-400"></i>
                Audit Existing Bookmarks
              </h3>
              <p className={`text-xs ${textSecondary} mt-1`}>
                Use our crawler engine to discover and audit existing social bookmarks for your brand across the web.
              </p>
            </div>
            <button
              onClick={handleAudit}
              disabled={isAuditing}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 border-none cursor-pointer ${
                isAuditing 
                  ? 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
              }`}
            >
              {isAuditing ? (
                <><i className="ri-loader-4-line animate-spin"></i> Auditing...</>
              ) : (
                <><i className="ri-search-eye-line"></i> Run Discovery Audit</>
              )}
            </button>
          </div>

          {isAuditing && (
            <div className="py-8 flex flex-col items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Crawling the web for existing bookmarks...</p>
            </div>
          )}

          {auditCompleted && discoveredAssets.length === 0 && (
            <div className="py-6 text-center border-t border-gray-100 dark:border-gray-700 mt-4">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-50 dark:bg-gray-800 mb-3">
                <i className="ri-search-line text-xl text-gray-400"></i>
              </div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">No existing bookmarks found</h4>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-sm mx-auto">
                Our crawler couldn't find any existing social bookmarks for your website.
              </p>
            </div>
          )}

          {auditCompleted && discoveredAssets.length > 0 && (
            <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="w-full text-left border-collapse min-w-[600px]">
                <thead className="bg-gray-50 dark:bg-gray-800/50">
                  <tr>
                    <th className="py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Platform Domain</th>
                    <th className="py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Asset URL</th>
                    <th className="py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {discoveredAssets.map((asset) => (
                    <tr key={asset.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 flex items-center justify-center flex-shrink-0">
                            <i className="ri-global-line text-gray-500 dark:text-gray-400 text-sm"></i>
                          </div>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {asset.platform_domain || asset.platform_name || 'Unknown'}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <a
                          href={asset.asset_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 dark:text-blue-400 font-mono break-all hover:underline flex items-center gap-1"
                        >
                          {asset.asset_url} <i className="ri-external-link-line text-[10px]"></i>
                        </a>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${
                          asset.status === 'ACTIVE'
                            ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800'
                            : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700'
                        }`}>
                          {asset.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Select Pages — API-driven (new section integrated into the flow) */}
      <div className={`${cardBg} rounded-xl border ${borderColor} p-5`}>
        <h3 className={`text-sm font-semibold ${textPrimary} mb-4 flex items-center gap-2`}>
          <i className="ri-file-list-3-line text-blue-600 dark:text-blue-400"></i>
          Select Pages to Bookmark
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {moduleData?.eligible_pages.map((page) => (
            <label
              key={page.url}
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                selectedPages.has(page.url)
                  ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-900/20 dark:border-blue-600'
                  : 'border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30'
              }`}
            >
              <input
                type="checkbox"
                checked={selectedPages.has(page.url)}
                onChange={() => togglePageSelection(page.url)}
                className="mt-1 border-gray-300 rounded text-blue-600 focus:ring-blue-500"
              />
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{page.title || 'Untitled Page'}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">{page.url}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Output / Generated Assets */}
      <div className={`${cardBg} rounded-xl border ${borderColor} p-5 space-y-6`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className={`text-sm font-semibold ${textPrimary}`}>Generated Bookmarks</h3>
            <p className={`text-xs ${textSecondary} mt-0.5`}>
              Click a bookmark asset to view details and track submission checklists below
            </p>
          </div>
          {!bookmarksGenerated ? (
            <button
              onClick={handleGenerate}
              disabled={selectedPages.size === 0 || isGenerating}
              className={`px-5 py-2.5 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                selectedPages.size === 0 || isGenerating
                  ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {isGenerating ? (
                <><i className="ri-loader-4-line animate-spin mr-2"></i>Generating...</>
              ) : (
                <><i className="ri-magic-line mr-2"></i>Generate Bookmarks</>
              )}
            </button>
          ) : (
            <div className="flex items-center gap-3">
              <span className="px-3 py-1.5 text-xs font-medium rounded-full bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                <i className="ri-check-line mr-1"></i>
                {generatedBookmarks.filter(b => (b.status === 'READY' || b.status === 'EDITED') && b.title && b.title.trim() !== '').length} Bookmarks Ready
              </span>
              <button
                onClick={handleGenerate}
                disabled={selectedPages.size === 0 || isGenerating}
                className={`px-4 py-1.5 text-xs font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                  selectedPages.size === 0 || isGenerating
                    ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-500 cursor-not-allowed'
                    : 'bg-white dark:bg-gray-800 border border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                }`}
              >
                {isGenerating ? (
                  <><i className="ri-loader-4-line animate-spin mr-2"></i>Regenerating...</>
                ) : (
                  <><i className="ri-refresh-line mr-2"></i>Regenerate</>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Loading state while generating */}
        {isGenerating && generatedBookmarks.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/20 rounded-full flex items-center justify-center mb-3 border border-blue-100 dark:border-blue-800">
              <i className="ri-loader-4-line text-xl text-blue-500 animate-spin"></i>
            </div>
            <p className={`text-sm font-medium ${textPrimary}`}>AI is generating your bookmarks...</p>
            <p className={`text-xs mt-1 ${textSecondary}`}>This usually takes 10-15 seconds.</p>
          </div>
        )}

        {/* Assets Grid */}
        {bookmarksGenerated && generatedBookmarks.length > 0 && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {generatedBookmarks.filter(bm => bm.status !== 'FAILED' && bm.title && bm.title.trim() !== '').map((bm) => {
                const isSelected = selectedBookmark?.id === bm.id;

                return (
                  <div
                    key={bm.id}
                    onClick={() => setSelectedBookmark(bm)}
                    className={`group relative cursor-pointer rounded-xl p-4 border transition-all ${
                      isSelected
                        ? 'border-blue-500 ring-2 ring-blue-100 dark:ring-blue-900/30 bg-blue-50/20 dark:bg-blue-900/10'
                        : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 bg-white dark:bg-gray-800'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-16 h-16 rounded-lg bg-gray-100 dark:bg-gray-700 overflow-hidden flex-shrink-0 border border-gray-200 dark:border-gray-600 relative">
                        {bm.image_url ? (
                          <img src={bm.image_url} alt={bm.title || 'Bookmark'} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <i className="ri-image-line text-xl" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-1 mb-1">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400 truncate">
                            {bm.keyword || 'Bookmark'}
                          </span>
                          <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${
                            bm.published_platforms && bm.published_platforms.length > 0
                              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                              : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                          }`}>
                            {bm.published_platforms?.length || 0} Submissions
                          </span>
                        </div>
                        <h4 className="text-xs font-bold text-gray-900 dark:text-white truncate" title={bm.title || ''}>
                          {bm.title || 'Untitled Bookmark'}
                        </h4>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate mt-0.5">
                          {bm.target_url}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Active Bookmark Info + Platform Checklist Split View */}
            {selectedBookmark && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 pt-4 border-t border-gray-100 dark:border-gray-700">
                {/* Active Bookmark Card */}
                <div className="lg:col-span-1 bg-gray-50 dark:bg-gray-800/60 rounded-xl p-5 border border-gray-200 dark:border-gray-700 space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-bold text-sm text-gray-900 dark:text-white">Active Bookmark Info</h4>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openDrawer(selectedBookmark, 'view')}
                        className="px-2.5 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs font-bold rounded-lg border border-blue-200 dark:border-blue-800 hover:bg-blue-100 cursor-pointer flex items-center gap-1"
                      >
                        <i className="ri-eye-line" /> Details
                      </button>
                      <button
                        onClick={() => openDrawer(selectedBookmark, 'edit')}
                        className="px-2.5 py-1 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-xs font-bold rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 cursor-pointer flex items-center gap-1"
                      >
                        <i className="ri-pencil-line" /> Edit
                      </button>
                    </div>
                  </div>

                  {selectedBookmark.image_url && (
                    <div className="aspect-video bg-gray-900 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 relative">
                      <img src={selectedBookmark.image_url} alt={selectedBookmark.title || ''} className="w-full h-full object-cover" />
                      <button
                        onClick={() => setExpandedImageUrl(selectedBookmark.image_url!)}
                        className="absolute bottom-2 right-2 p-1.5 bg-black/60 hover:bg-black/80 text-white rounded-md text-xs cursor-pointer"
                      >
                        <i className="ri-zoom-in-line" />
                      </button>
                    </div>
                  )}

                  <div className="space-y-3 text-xs">
                    <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
                      <span className="text-[10px] uppercase font-semibold text-gray-400 block mb-0.5">Title</span>
                      <p className="font-semibold text-gray-800 dark:text-gray-200">{selectedBookmark.title}</p>
                    </div>
                    <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
                      <span className="text-[10px] uppercase font-semibold text-gray-400 block mb-0.5">Target URL</span>
                      <a href={selectedBookmark.target_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 font-mono truncate block hover:underline">
                        {selectedBookmark.target_url}
                      </a>
                    </div>
                    {selectedBookmark.description && (
                      <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
                        <span className="text-[10px] uppercase font-semibold text-gray-400 block mb-0.5">Description</span>
                        <p className="text-gray-600 dark:text-gray-400 line-clamp-3">{selectedBookmark.description}</p>
                      </div>
                    )}
                    {selectedBookmark.tags && selectedBookmark.tags.length > 0 && (
                      <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
                        <span className="text-[10px] uppercase font-semibold text-gray-400 block mb-1">Tags</span>
                        <div className="flex flex-wrap gap-1">
                          {selectedBookmark.tags.map(t => (
                            <span key={t} className="px-1.5 py-0.5 text-[10px] bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded">
                              #{t}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Per-Bookmark Submission Checklist */}
                <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700 space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                      <h4 className="font-bold text-sm text-gray-900 dark:text-white flex items-center gap-2">
                        <i className="ri-global-line text-blue-500" /> Suggested Platforms — Submission Checklist
                      </h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Submit this specific bookmark manually to the platforms below. Check off completed platforms and save.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {markCompleteSuccess && (
                        <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                          <i className="ri-checkbox-circle-fill" /> Saved!
                        </span>
                      )}
                      <button
                        onClick={handleMarkCompletePerBookmark}
                        disabled={isMarkingComplete}
                        className="px-4 py-2 text-xs font-bold rounded-xl bg-blue-600 hover:bg-blue-700 text-white transition-all shadow-sm cursor-pointer border-none flex items-center gap-1.5"
                      >
                        {isMarkingComplete ? (
                          <><i className="ri-loader-4-line animate-spin" /> Saving...</>
                        ) : (
                          <><i className="ri-checkbox-circle-line" /> Mark as Complete</>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {platformList.map((platform) => {
                      const isChecked = !!checkedPlatforms[platform.platform_name];
                      const icon = bookmarkSiteIcons[platform.platform_name] || 'ri-bookmark-line';

                      return (
                        <div
                          key={platform.platform_name}
                          className={`flex items-center justify-between p-3.5 rounded-xl border transition-all ${
                            isChecked
                              ? 'bg-emerald-50/30 dark:bg-emerald-950/10 border-emerald-200 dark:border-emerald-900/50'
                              : 'bg-gray-50 dark:bg-gray-700/30 border-gray-100 dark:border-gray-700'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleTogglePlatformCheckbox(platform.platform_name)}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                            />
                            <div className="w-8 h-8 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 flex items-center justify-center">
                              <i className={`${icon} ${isChecked ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'} text-lg`}></i>
                            </div>
                            <div>
                              <span className={`text-sm font-semibold text-gray-900 dark:text-white ${isChecked ? 'line-through text-gray-400 dark:text-gray-500' : ''}`}>
                                {platform.platform_name}
                              </span>
                              <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400">
                                DA {platform.da_score}
                              </span>
                            </div>
                          </div>

                          <a
                            href={platform.platform_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 font-semibold"
                          >
                            Visit Site <i className="ri-external-link-line" />
                          </a>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>


      {/* ─── Slide-out Drawer ─── */}
      {drawerBookmark && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/40 z-40 transition-opacity"
            onClick={closeDrawer}
          />
          {/* Drawer Panel */}
          <div className="fixed top-0 right-0 h-full w-full max-w-lg bg-white dark:bg-gray-800 shadow-2xl z-50 flex flex-col animate-slide-in-right">
            {/* Drawer Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
                  <i className="ri-bookmark-line text-blue-600 dark:text-blue-400"></i>
                </div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  {drawerMode === 'edit' ? 'Edit Bookmark' : 'Bookmark Details'}
                </h3>
              </div>
              <button
                onClick={closeDrawer}
                className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:text-gray-200 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            {/* Drawer Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              {/* Target URL */}
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Target URL</label>
                <a
                  href={drawerBookmark.target_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 dark:text-blue-400 break-all hover:underline"
                >
                  {drawerBookmark.target_url}
                </a>
              </div>

              {/* Image Preview */}
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Contextual AI Image</label>
                {drawerBookmark.image_url ? (
                  <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800">
                    <img
                      src={drawerBookmark.image_url}
                      alt={drawerBookmark.title || 'Bookmark image'}
                      className="w-full h-48 object-cover"
                    />
                  </div>
                ) : (
                  <div className="h-20 rounded-xl border border-red-200 dark:border-red-900/50 bg-red-50/50 dark:bg-red-950/20 flex items-center justify-center gap-2 text-xs text-red-600 dark:text-red-400 font-medium">
                    <i className="ri-error-warning-line text-lg" />
                    Image generation failed
                  </div>
                )}
              </div>

              {/* Status */}
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Status</label>
                <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${
                  drawerBookmark.status === 'READY' || drawerBookmark.status === 'EDITED'
                    ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800'
                    : 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800'
                }`}>{drawerBookmark.status}</span>
              </div>

              {/* Title */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Title</label>
                  {drawerMode === 'view' && (
                    <button
                      onClick={() => handleCopyField(drawerBookmark.title || '', 'title')}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 cursor-pointer bg-transparent border-none font-semibold"
                    >
                      <i className={copiedField === 'title' ? 'ri-check-line text-emerald-500' : 'ri-file-copy-line'} />
                      {copiedField === 'title' ? 'Copied!' : 'Copy'}
                    </button>
                  )}
                </div>
                {drawerMode === 'edit' ? (
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    className="w-full text-sm p-2.5 border rounded-lg border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder="Enter bookmark title..."
                  />
                ) : (
                  <p className="text-sm text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 border border-gray-100 dark:border-gray-700">{drawerBookmark.title || '—'}</p>
                )}
              </div>

              {/* Description */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Description</label>
                  {drawerMode === 'view' && (
                    <button
                      onClick={() => handleCopyField(drawerBookmark.description || '', 'desc')}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 cursor-pointer bg-transparent border-none font-semibold"
                    >
                      <i className={copiedField === 'desc' ? 'ri-check-line text-emerald-500' : 'ri-file-copy-line'} />
                      {copiedField === 'desc' ? 'Copied!' : 'Copy'}
                    </button>
                  )}
                </div>
                {drawerMode === 'edit' ? (
                  <textarea
                    rows={4}
                    value={editDesc}
                    onChange={(e) => setEditDesc(e.target.value)}
                    className="w-full text-sm p-2.5 border rounded-lg border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                    placeholder="Enter bookmark description..."
                  />
                ) : (
                  <p className="text-sm text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 border border-gray-100 dark:border-gray-700 whitespace-pre-wrap">{drawerBookmark.description || '—'}</p>
                )}
              </div>

              {/* Tags */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Tags</label>
                  {drawerMode === 'view' && (
                    <button
                      onClick={() => handleCopyField((drawerBookmark.tags || []).join(', '), 'tags')}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 cursor-pointer bg-transparent border-none font-semibold"
                    >
                      <i className={copiedField === 'tags' ? 'ri-check-line text-emerald-500' : 'ri-file-copy-line'} />
                      {copiedField === 'tags' ? 'Copied!' : 'Copy'}
                    </button>
                  )}
                </div>
                {drawerMode === 'edit' ? (
                  <div>
                    <input
                      type="text"
                      value={editTags}
                      onChange={(e) => setEditTags(e.target.value)}
                      className="w-full text-sm p-2.5 border rounded-lg border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      placeholder="tag1, tag2, tag3"
                    />
                    <p className="text-[10px] text-gray-400 mt-1">Separate tags with commas</p>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {drawerBookmark.tags?.map((tag) => (
                      <span key={tag} className="px-2.5 py-1 text-xs font-medium rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Drawer Footer */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
              {drawerMode === 'view' ? (
                <>
                  <button
                    onClick={() => handleCopyAll(drawerBookmark)}
                    className="flex items-center gap-1.5 text-sm px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                  >
                    <i className={copiedField === 'all' ? 'ri-check-double-line text-emerald-500' : 'ri-file-copy-line'} />
                    {copiedField === 'all' ? 'All Copied!' : 'Copy All'}
                  </button>
                  <button
                    onClick={() => setDrawerMode('edit')}
                    className="text-sm px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors cursor-pointer"
                  >
                    <i className="ri-pencil-line mr-1.5" />Edit Bookmark
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setDrawerMode('view')}
                    className="text-sm px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveFromDrawer}
                    disabled={isSaving}
                    className="text-sm px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors cursor-pointer disabled:opacity-50"
                  >
                    {isSaving ? (
                      <><i className="ri-loader-4-line animate-spin mr-1.5"></i>Saving...</>
                    ) : (
                      <><i className="ri-save-line mr-1.5"></i>Save Changes</>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Slide-in animation */}
          <style>{`
            @keyframes slideInRight {
              from { transform: translateX(100%); }
              to { transform: translateX(0); }
            }
            .animate-slide-in-right {
              animation: slideInRight 0.25s ease-out;
            }
          `}</style>
        </>
      )}

      {/* Image Modal */}
      {expandedImageUrl && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setExpandedImageUrl(null)}
        >
          <div className="relative max-w-5xl w-full max-h-[90vh] flex flex-col items-center justify-center animate-scale-in" onClick={e => e.stopPropagation()}>
            <img src={expandedImageUrl} className="w-auto h-auto max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl" alt="Expanded AI Image" />
            
            <div className="absolute top-4 right-4 flex gap-2">
              <button
                onClick={async () => {
                  try {
                    await downloadS3ImageProxy(expandedImageUrl);
                  } catch (error) {
                    console.error('Download failed', error);
                    alert('Failed to download image. Please try again.');
                  }
                }}
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 text-white flex items-center justify-center transition-colors cursor-pointer"
                title="Download"
              >
                <i className="ri-download-2-line text-lg" />
              </button>
              <button
                onClick={() => setExpandedImageUrl(null)}
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 text-white flex items-center justify-center transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
          </div>
          <style>{`
            @keyframes scaleIn {
              from { transform: scale(0.95); opacity: 0; }
              to { transform: scale(1); opacity: 1; }
            }
            .animate-scale-in {
              animation: scaleIn 0.2s ease-out;
            }
          `}</style>
        </div>
      )}
    </div>
  );
}
